import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { LoginCredentials } from '../types'
import type { Database } from '@/shared/types/database'

type UserRole = Database['public']['Enums']['user_role']

/**
 * Intranet login - for USER, MANAGER, ADMIN roles only
 */
export function useIntranetLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (credentials: LoginCredentials) => {
      // Step 1: Sign in with email/password
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword(
        credentials
      )

      if (authError) throw authError
      if (!authData.user) throw new Error('No user returned after login')

      // Step 2: Check if user has appropriate role for intranet
      const { data: userResidences, error: residencesError } = await supabase
        .from('user_residences')
        .select('role')
        .eq('user_id', authData.user.id)

      if (residencesError) {
        // Sign out the user since they can't access intranet
        await supabase.auth.signOut()
        throw residencesError
      }

      // Step 3: Verify user has at least one intranet role
      const intranetRoles: UserRole[] = ['USER', 'MANAGER', 'ADMIN', 'ACCOUNTANT']
      const hasIntranetAccess = userResidences?.some((ur) =>
        intranetRoles.includes(ur.role)
      )

      if (!hasIntranetAccess) {
        // Step 4: If no residences, check if user is an agency owner (onboarding flow)
        const { data: userProfile, error: profileError } = await supabase
          .from('users')
          .select('agency_id')
          .eq('id', authData.user.id)
          .single()

        if (profileError) {
          await supabase.auth.signOut()
          throw profileError
        }

        // Allow access if user has an agency (they're in onboarding)
        // They will be redirected to create their first property
        if (!userProfile?.agency_id) {
          await supabase.auth.signOut()
          throw new Error('INTRANET_ACCESS_DENIED')
        }
      }

      return authData
    },
    onSuccess: async () => {
      // Invalidate and refetch auth and agency queries immediately
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['auth'] }),
        queryClient.invalidateQueries({ queryKey: ['agencies'] }),
      ])
      // Force refetch to ensure data is loaded before navigation
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['auth'] }),
        queryClient.refetchQueries({ queryKey: ['agencies'] }),
      ])
    },
  })
}
